import React, { useState, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Layout } from './Layout';
import { Header } from './Header';
import { BottomNavigation } from './BottomNavigation';
import { LoginPromptModal } from './LoginPromptModal';
import { useAuth } from '@providers';
import type { LoginPromptActionType } from '@hooks/useLoginPrompt';

/**
 * AIDEV-NOTE: GuestLayout - Layout wrapper for unauthenticated users
 * ===================================================================
 * This component handles all guest-specific UI concerns:
 * - Owns LoginPromptModal state and rendering
 * - Provides callbacks to Header and BottomNavigation for protected actions
 * - Uses React Router's <Outlet> to render child routes
 *
 * FEATURES:
 * - Guest-aware Header (shows login prompt on avatar click)
 * - BottomNavigation with login prompts for Connect/Messages
 * - Centralized login modal management
 * - Dynamic gradient based on scroll position (home only)
 */
export const GuestLayout: React.FC = () => {
  const location = useLocation();
  const { signIn } = useAuth();
  const [isAtTop, setIsAtTop] = useState(true);

  // Login prompt state - centralized here instead of in child components
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [promptActionType, setPromptActionType] = useState<LoginPromptActionType>('generic');

  const isHomeFeed = location.pathname === '/home';
  const showBottomNav = ['/home', '/events', '/messages', '/notifications'].includes(
    location.pathname,
  );
  // Events page has its own header via PublicLayout
  const showDefaultHeader = location.pathname !== '/events';

  // Handle protected navigation clicks from BottomNavigation
  // Maps nav item IDs to action types for proper analytics tracking
  const handleProtectedNavClick = useCallback((actionType: string) => {
    // Map action descriptions to typed action types
    const actionTypeMap: Record<string, LoginPromptActionType | undefined> = {
      'connect with others': 'connect',
      'access messages': 'access_messages',
    };
    setPromptActionType(actionTypeMap[actionType] ?? 'generic');
    setShowLoginPrompt(true);
  }, []);

  // Handle avatar click in header (protected action for guests)
  const handleAvatarClick = useCallback(() => {
    setPromptActionType('access_profile');
    setShowLoginPrompt(true);
  }, []);

  // Handle scroll sentinel change for dynamic gradient
  const handleScrollSentinelChange = useCallback((sentinelVisible: boolean) => {
    setIsAtTop(sentinelVisible);
  }, []);

  const gradientType: 'none' | 'subtle' | 'standard' | 'prominent' =
    isHomeFeed && !isAtTop ? 'prominent' : 'standard';

  // Custom header that receives guest-specific callbacks.
  // Header reads showAuthenticatedUI from useAuth() — guests get false naturally.
  const customHeader = showDefaultHeader ? <Header onAvatarClick={handleAvatarClick} /> : undefined;

  const customFooter = showBottomNav ? (
    <BottomNavigation onProtectedNavClick={handleProtectedNavClick} />
  ) : undefined;

  return (
    <>
      <Layout
        showDefaultHeader={false}
        customHeader={customHeader}
        gradientType={gradientType}
        enableScrollSentinel={isHomeFeed}
        onScrollSentinelChange={handleScrollSentinelChange}
        showDefaultFooter={false}
        customFooter={customFooter}
      >
        <Outlet />
      </Layout>

      {/* Centralized login prompt modal - owned by GuestLayout */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => {
          setShowLoginPrompt(false);
          setPromptActionType('generic');
        }}
        actionType={promptActionType}
        onLogin={() => {
          signIn({ actionType: promptActionType });
        }}
      />
    </>
  );
};
